import React, { useState } from 'react';
import Button from '../../../components/Button';
import { PlaceData } from '../../../components/AddressAutocomplete';
import { 
  enrichCompetitorFromGoogle, 
  searchNearbyCompetitors,
  CompetitorSuggestion,
  EnrichedCompetitorData
} from '../../../services/google-places';
import {
  AddCompetitorForm,
  CompetitorSearchModal,
  CompetitorsTable,
  CompetitionTabProps,
  getProjectCoordinates
} from './Competition';

const CompetitionTab: React.FC<CompetitionTabProps> = ({
  competitors,
  addingCompetitor,
  newCompetitor,
  setAddingCompetitor,
  setNewCompetitor,
  handleAddCompetitor,
  handleUpdateCompetitor,
  handleDeleteCompetitor,
  projectData
}) => {
  // Edit state
  const [editingCompetitor, setEditingCompetitor] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<any>>({});
  
  // Google enrichment state
  const [loadingGoogleData, setLoadingGoogleData] = useState(false);
  const [googlePreview, setGooglePreview] = useState<EnrichedCompetitorData | null>(null);
  
  // Competitor suggestions state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<CompetitorSuggestion[]>([]);
  const [searchRadius, setSearchRadius] = useState(5);
  const [searchBusinessType, setSearchBusinessType] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const handleEdit = (competitor: any) => {
    setEditingCompetitor(competitor.id);
    setEditForm({
      name: competitor.name,
      address: competitor.address,
      competition_type: competitor.competition_type,
      distance_miles: competitor.distance_miles,
      threat_rating: competitor.threat_rating
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCompetitor) return;
    await handleUpdateCompetitor(editingCompetitor, editForm);
    setEditingCompetitor(null);
    setEditForm({});
  };

  const handleCancelEdit = () => {
    setEditingCompetitor(null);
    setEditForm({});
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this competitor?')) {
      await handleDeleteCompetitor(id);
    }
  };

  const handleAddressSelect = async (place: PlaceData) => {
    console.log('Selected place:', place);
    
    setLoadingGoogleData(true);
    try {
      const projectCoords = getProjectCoordinates(projectData);
      const enrichedData = await enrichCompetitorFromGoogle(place.place_id, projectCoords || undefined);
      
      // Update the form with Google data
      setNewCompetitor({
        ...newCompetitor,
        name: enrichedData.name,
        address: enrichedData.address,
        distance_miles: enrichedData.distance_miles,
        phone: enrichedData.phone,
        website: enrichedData.website,
        rating: enrichedData.rating,
        business_types: enrichedData.business_types,
        business_status: enrichedData.business_status,
        operating_hours: enrichedData.operating_hours,
        google_data: enrichedData.google_data
      });
      
      setGooglePreview(enrichedData);
    } catch (error) {
      console.error('Error fetching Google data:', error);
      alert('Failed to fetch data from Google. Please try again.');
      
      // Fallback to basic place data
      setNewCompetitor({
        ...newCompetitor,
        name: place.name || newCompetitor.name,
        address: {
          formatted_address: place.formatted_address,
          google_place_id: place.place_id,
          location: place.lat && place.lng ? {
            type: 'Point',
            coordinates: [place.lng, place.lat]
          } : undefined
        }
      });
    } finally {
      setLoadingGoogleData(false);
    }
  };

  const handleClearPreview = () => {
    setGooglePreview(null);
    setNewCompetitor({
      name: '',
      address: { formatted_address: '' },
      competition_type: 'Potential',
      threat_rating: 3
    });
  };

  const handleSearchSuggestions = async () => {
    const projectCoords = getProjectCoordinates(projectData);
    if (!projectCoords) {
      alert('Project location not available. Please ensure the project has a valid address.');
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await searchNearbyCompetitors(
        projectCoords.lat,
        projectCoords.lng,
        searchRadius,
        searchBusinessType || undefined,
        searchKeyword || undefined
      );
      setSuggestions(response.suggestions);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching for competitor suggestions:', error);
      alert('Failed to search for competitor suggestions. Please try again.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAddSuggestion = (suggestion: CompetitorSuggestion) => {
    setNewCompetitor({
      name: suggestion.name,
      address: {
        formatted_address: suggestion.formatted_address,
        google_place_id: suggestion.place_id,
        location: {
          type: 'Point',
          coordinates: [suggestion.location.lng, suggestion.location.lat]
        }
      },
      distance_miles: suggestion.distance_miles,
      rating: suggestion.rating,
      business_types: suggestion.business_types,
      business_status: suggestion.business_status,
      competition_type: suggestion.suggested_competition_type,
      threat_rating: suggestion.suggested_threat_rating,
      google_data: {
        user_ratings_total: suggestion.user_ratings_total || 0,
        photos: suggestion.photos,
        place_id: suggestion.place_id
      }
    });
    
    // Create a preview from the suggestion
    const previewData: EnrichedCompetitorData = {
      name: suggestion.name,
      address: {
        formatted_address: suggestion.formatted_address,
        google_place_id: suggestion.place_id,
        location: {
          type: 'Point',
          coordinates: [suggestion.location.lng, suggestion.location.lat]
        }
      },
      distance_miles: suggestion.distance_miles,
      rating: suggestion.rating,
      business_types: suggestion.business_types,
      business_status: suggestion.business_status,
      google_data: {
        user_ratings_total: suggestion.user_ratings_total || 0,
        photos: suggestion.photos,
        place_id: suggestion.place_id
      }
    };
    
    setGooglePreview(previewData);
    setAddingCompetitor(true);
    setShowSuggestions(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.4rem', color: '#1e1e1e' }}>Competition</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button 
            onClick={() => setShowSuggestions(true)}
            style={{ 
              backgroundColor: '#52c41a',
              color: 'white'
            }}
          >
            🔍 Find Competitors
          </Button>
          <Button onClick={() => setAddingCompetitor(true)}>Add Competitor</Button>
        </div>
      </div>

      <CompetitorSearchModal
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        searchRadius={searchRadius}
        setSearchRadius={setSearchRadius}
        searchBusinessType={searchBusinessType}
        setSearchBusinessType={setSearchBusinessType}
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        loadingSuggestions={loadingSuggestions}
        suggestions={suggestions}
        onSearch={handleSearchSuggestions}
        onAddSuggestion={handleAddSuggestion}
      />
      
      <AddCompetitorForm
        addingCompetitor={addingCompetitor}
        newCompetitor={newCompetitor}
        setNewCompetitor={setNewCompetitor}
        setAddingCompetitor={setAddingCompetitor}
        handleAddCompetitor={handleAddCompetitor}
        googlePreview={googlePreview}
        loadingGoogleData={loadingGoogleData}
        onAddressSelect={handleAddressSelect}
        onClearPreview={handleClearPreview}
      />
      
      <CompetitorsTable
        competitors={competitors}
        editingCompetitor={editingCompetitor}
        editForm={editForm}
        setEditingCompetitor={setEditingCompetitor}
        setEditForm={setEditForm}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default CompetitionTab;
